import { useMemo, useState } from "react";
import {
  Button,
  Card,
  Empty,
  Flex,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { ColumnsType } from "antd/lib/table";
import { DownloadOutlined, ExportOutlined } from "@ant-design/icons";
import { BotInvoice, useListInvoicesQuery } from "api";
import { dateFormat, format } from "libs";

const PAGE_SIZE = 20;

const STATUS_TONE: Record<string, string> = {
  paid: "green",
  open: "orange",
  uncollectible: "red",
  void: "default",
  draft: "blue",
};

/**
 * Stripe uses cursor pagination (`starting_after`), not offset. We accumulate
 * loaded pages locally and expose a "Load more" button instead of numbered
 * pages — same UX pattern Stripe Dashboard itself uses.
 */
export const BotsBillingInvoicesList = () => {
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [accumulated, setAccumulated] = useState<BotInvoice[]>([]);
  const lastCursor = cursors[cursors.length - 1];

  const { data, isFetching } = useListInvoicesQuery(
    { limit: PAGE_SIZE, starting_after: lastCursor },
    {
      // Refetch every time we change cursors so we get the next page.
      refetchOnMountOrArgChange: true,
    }
  );

  // When new data arrives, append (deduping by id in case of concurrent fetches).
  useMemo(() => {
    if (!data?.data) {
      return;
    }
    setAccumulated((prev) => {
      const seen = new Set(prev.map((inv) => inv.id));
      const fresh = data.data.filter((inv) => !seen.has(inv.id));
      return [...prev, ...fresh];
    });
  }, [data]);

  const onLoadMore = () => {
    if (data?.next_cursor) {
      setCursors((prev) => [...prev, data.next_cursor ?? undefined]);
    }
  };

  const columns: ColumnsType<BotInvoice> = [
    {
      title: "Nr.",
      dataIndex: "number",
      width: 130,
      render: (n: string | null) => n ?? "—",
    },
    {
      title: "Data",
      dataIndex: "created_at",
      width: 130,
      render: (created_at: string | null) =>
        created_at ? format(created_at, dateFormat) : "—",
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 110,
      render: (status: string) => (
        <Tag color={STATUS_TONE[status] ?? "default"}>{status}</Tag>
      ),
    },
    {
      title: "Suma platita",
      dataIndex: "amount_paid",
      align: "right" as const,
      width: 140,
      render: (cents: number, inv) =>
        `${(cents / 100).toFixed(2)} ${inv.currency}`,
    },
    {
      title: "Suma datorata",
      dataIndex: "amount_due",
      align: "right" as const,
      width: 140,
      render: (cents: number, inv) =>
        `${(cents / 100).toFixed(2)} ${inv.currency}`,
    },
    {
      title: "Actiuni",
      align: "right" as const,
      width: 180,
      render: (_, inv) => (
        <Space>
          {inv.invoice_pdf && (
            <Button
              type="text"
              icon={<DownloadOutlined />}
              href={inv.invoice_pdf}
              target="_blank"
              rel="noopener noreferrer"
            >
              PDF
            </Button>
          )}
          {inv.hosted_invoice_url && (
            <Button
              type="text"
              icon={<ExportOutlined />}
              href={inv.hosted_invoice_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Stripe
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card title="Facturi (Stripe)">
      <Table<BotInvoice>
        columns={columns}
        dataSource={accumulated}
        rowKey="id"
        pagination={false}
        loading={isFetching && accumulated.length === 0}
        scroll={{ x: 800 }}
        locale={{
          emptyText: <Empty description="Nicio factura emisa inca." />,
        }}
      />
      {data?.has_more && (
        <Flex justify="center" style={{ marginTop: 16 }}>
          <Button onClick={onLoadMore} loading={isFetching}>
            Incarca mai multe
          </Button>
        </Flex>
      )}
      {accumulated.length > 0 && (
        <Typography.Text type="secondary" style={{ display: "block", marginTop: 8 }}>
          {accumulated.length} facturi incarcate.
        </Typography.Text>
      )}
    </Card>
  );
};
